import { FC, useEffect } from 'react';

/// material-ui
import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
// project imports
import SubCard from 'ui-component/cards/SubCard';
import Chip from 'ui-component/extended/Chip';
import { gridSpacing } from 'store/constant';
import { ThemeMode } from 'config';

// assets
import EmailTwoToneIcon from '@mui/icons-material/EmailTwoTone';
import PhoneAndroidTwoToneIcon from '@mui/icons-material/PhoneAndroidTwoTone';
import MainCard from 'ui-component/cards/MainCard';

import { alpha, Avatar, Tooltip } from '@mui/material';
import { useQuoteStore } from 'Application/store';
import Loader from 'ui-component/Loader';
import { useParams } from 'react-router-dom';
import { QuoteStatus } from 'Application/models';
import { addCommasToNumbers, getBackgroundColor } from 'Application/utils';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import BackTo from 'Application/components/NavigateBack';
import QuoteTravelDetails from './QuoteTravelDetails';
import QuoteTravelPlan from './QuoteTravelPlan';

const ViewQuoteDetails: FC = () => {
    const theme = useTheme();
    const { quoteId } = useParams();

    const {
        isLoading: isGetQuoteDetailsLoading,
        getQuoteDetails,
        quoteDetailsData
    } = useQuoteStore((state) => ({
        isLoading: state.isLoading,
        getQuoteDetails: state.getQuoteDetails,
        quoteDetailsData: state.quoteDetailsData
    }));

    const travelDestinations = quoteDetailsData?.quotation.travelDestinations;
    const mainCustomer = quoteDetailsData?.customer;
    const dependantsDetails = quoteDetailsData?.dependants;

    useEffect(() => {
        getQuoteDetails(quoteId);
    }, [quoteId]);

    const handleCopyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success('Email copied to clipboard'); // Optional toast notification for feedback
    };

    if (isGetQuoteDetailsLoading) {
        return <Loader />;
    }

    return (
        <>
            <BackTo title={'Back To Generated Quotes'} />

            <MainCard>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12}>
                        <SubCard
                            title="Quote Details"
                            secondary={
                                <Typography variant="subtitle1">
                                    Placed on {dayjs(quoteDetailsData?.quotation.createdAt || 'N/A').format('YYYY-MM-DD')}
                                </Typography>
                            }
                        >
                            <Grid container spacing={gridSpacing}>
                                <Grid item xs={12}>
                                    <Grid container spacing={3} direction="row" alignItems="center" wrap="wrap">
                                        {/* Avatar and Name */}
                                        <Grid item>
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <Avatar color="primary" sx={{ fontSize: '1rem' }}>
                                                    {quoteDetailsData?.customer.firstName.charAt(0)}
                                                    {quoteDetailsData?.customer.otherNames?.charAt(0)}
                                                </Avatar>
                                                <Typography variant="body2">
                                                    {quoteDetailsData?.customer.firstName} {quoteDetailsData?.customer.otherNames}
                                                </Typography>
                                            </Stack>
                                        </Grid>

                                        {/* Phone Number */}
                                        <Grid item>
                                            <Typography variant="body2" display="flex" alignItems="center">
                                                <PhoneAndroidTwoToneIcon sx={{ mr: 1 }} /> {quoteDetailsData?.customer.phoneNumber}
                                            </Typography>
                                        </Grid>

                                        {/* Email Address */}
                                        <Grid item>
                                            <Typography variant="body2" display="flex" alignItems="center">
                                                <EmailTwoToneIcon sx={{ mr: 1 }} /> {quoteDetailsData?.customer.emailAddress}
                                                <Tooltip title="Copy email">
                                                    <IconButton
                                                        onClick={() =>
                                                            quoteDetailsData &&
                                                            handleCopyToClipboard(quoteDetailsData.customer.emailAddress)
                                                        }
                                                        size="small"
                                                        sx={{ ml: 1 }}
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h4">Quotation Details</Typography>
                                    <Grid container spacing={3} sx={{ mt: '1px' }}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Quote No:</Typography>
                                                        <Typography variant="body2">
                                                            {quoteDetailsData?.quotation.quotationNumber}
                                                        </Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Quotation Status:</Typography>
                                                        <Chip
                                                            label={quoteDetailsData?.quotation.quotationStatus}
                                                            size="small"
                                                            sx={
                                                                quoteDetailsData?.quotation.quotationStatus === QuoteStatus.COMPLETED
                                                                    ? {
                                                                          bgcolor:
                                                                              theme.palette.mode === ThemeMode.DARK
                                                                                  ? 'dark.main'
                                                                                  : alpha(theme.palette.success.light, 0.6),
                                                                          color: 'success.dark'
                                                                      }
                                                                    : {
                                                                          bgcolor:
                                                                              theme.palette.mode === ThemeMode.DARK
                                                                                  ? 'dark.main'
                                                                                  : alpha(theme.palette.primary.light, 0.6),
                                                                          color: 'primary.dark'
                                                                      }
                                                            }
                                                        />
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Departure Date:</Typography>
                                                        <Typography variant="body2">{quoteDetailsData?.quotation.departureDate}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Return Date:</Typography>
                                                        <Typography variant="body2">{quoteDetailsData?.quotation.returnDate}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Stack spacing={2}>
                                                <Stack spacing={0}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Travel From:</Typography>
                                                        <Typography variant="body2">
                                                            {quoteDetailsData?.quotation.sourceLocation}
                                                        </Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Typography variant="subtitle1">Destinations:</Typography>
                                                        <Typography variant="body2">
                                                            {travelDestinations?.map((destination, index) => (
                                                                <span key={index}>
                                                                    {destination.destinationName}
                                                                    {index < travelDestinations.length - 1 && ', '}
                                                                </span>
                                                            ))}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider />
                                </Grid>
                                <Grid item xs={12}>
                                    {quoteDetailsData && <QuoteTravelDetails quoteDetailsData={quoteDetailsData} />}
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                    <Grid item xs={12}>
                        {quoteDetailsData && quoteDetailsData.plan && <QuoteTravelPlan quoteDetailsData={quoteDetailsData} />}
                    </Grid>
                    <Grid item xs={12}>
                        <SubCard title="Customer Details" content={false}>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ pl: 3 }}>Name / Email</TableCell>
                                                    <TableCell align="right">Phone Number</TableCell>
                                                    <TableCell align="right">Nationality</TableCell>
                                                    <TableCell align="right">Passport Number</TableCell>
                                                    <TableCell align="right">
                                                        {quoteDetailsData?.quotation.sourceLocation === 'Kenya' ? 'Kra Pin' : 'Tax Number'}
                                                    </TableCell>
                                                    <TableCell align="right">Date Of Birth</TableCell>
                                                    <TableCell align="right">Gender</TableCell>
                                                    <TableCell sx={{ pr: 3 }} align="right">
                                                        Occupation
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {/* Render main customer details */}
                                                {mainCustomer ? (
                                                    <TableRow>
                                                        <TableCell sx={{ pl: 3 }}>
                                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                                <Avatar
                                                                    alt="User Avatar"
                                                                    className={`border border-gray-200 ${
                                                                        mainCustomer.firstName
                                                                            ? getBackgroundColor(mainCustomer.firstName[0].toUpperCase())
                                                                            : 'transparent'
                                                                    }`}
                                                                >
                                                                    {mainCustomer.firstName ? mainCustomer.firstName[0].toUpperCase() : ''}
                                                                </Avatar>
                                                                <Stack>
                                                                    <Typography variant="subtitle1">
                                                                        {mainCustomer.firstName || ''} {mainCustomer.otherNames || ''}
                                                                    </Typography>
                                                                    <Typography variant="subtitle2" noWrap>
                                                                        {mainCustomer.emailAddress || ''}
                                                                    </Typography>
                                                                </Stack>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell align="right">{mainCustomer.phoneNumber}</TableCell>
                                                        <TableCell align="right">{mainCustomer.nationality}</TableCell>
                                                        <TableCell align="right">{mainCustomer.identificationNumber}</TableCell>
                                                        <TableCell align="right">{mainCustomer.taxNumber}</TableCell>
                                                        <TableCell align="right">
                                                            <Stack alignItems="end">
                                                                {dayjs(mainCustomer.dateOfBirth).format('YYYY-MM-DD')}{' '}
                                                                <Chip
                                                                    label={dayjs().diff(dayjs(mainCustomer.dateOfBirth), 'year')}
                                                                    color="primary"
                                                                    size="small"
                                                                    sx={{ marginLeft: 1 }}
                                                                />
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell align="right">{mainCustomer.gender}</TableCell>
                                                        <TableCell align="right">{mainCustomer.occupation?.description ?? '---'}</TableCell>
                                                    </TableRow>
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={10} align="center">
                                                            No customer data available
                                                        </TableCell>
                                                    </TableRow>
                                                )}

                                                {/* Render dependants details */}
                                                {dependantsDetails && dependantsDetails.length > 0 ? (
                                                    dependantsDetails.map((dependant, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell sx={{ pl: 3 }}>
                                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                                    <Avatar
                                                                        alt="Dependant Avatar"
                                                                        className={`border border-gray-200 ${
                                                                            dependant.firstName
                                                                                ? getBackgroundColor(dependant.firstName[0].toUpperCase())
                                                                                : 'transparent'
                                                                        }`}
                                                                    >
                                                                        {dependant.firstName ? dependant.firstName[0].toUpperCase() : ''}
                                                                    </Avatar>
                                                                    <Stack>
                                                                        <Typography variant="subtitle1">
                                                                            {dependant.firstName || ''} {dependant.otherNames || ''}
                                                                        </Typography>
                                                                        <Typography variant="subtitle2" noWrap>
                                                                            {dependant.emailAddress || ''}
                                                                        </Typography>
                                                                    </Stack>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell align="right">{dependant.phoneNumber}</TableCell>
                                                            <TableCell align="right">{dependant.nationality}</TableCell>
                                                            <TableCell align="right">{dependant.passportNumber}</TableCell>
                                                            <TableCell align="right">{dependant.taxNumber}</TableCell>
                                                            <TableCell align="right">
                                                                {dayjs(dependant.dateOfBirth).format('YYYY-MM-DD')}{' '}
                                                                <Chip
                                                                    label={dayjs().diff(dayjs(dependant.dateOfBirth), 'year')}
                                                                    color="primary"
                                                                    size="small"
                                                                    sx={{ marginLeft: 1 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">{dependant.gender}</TableCell>
                                                            <TableCell align="right" sx={{ pr: 3 }}>
                                                                {dependant.occupation?.description || '----'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={10} align="center">
                                                            No dependants
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>

                                <Grid item xs={12}>
                                    <SubCard
                                        sx={{
                                            mx: 3,
                                            mb: 3,
                                            bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'primary.light'
                                        }}
                                    >
                                        <Grid container justifyContent="flex-end" spacing={gridSpacing}>
                                            <Grid item sm={6} md={4}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        <Divider sx={{ bgcolor: 'dark.main' }} />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Grid container spacing={1}>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    Basic Premium:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    {addCommasToNumbers(
                                                                        quoteDetailsData?.quotation.basicPremium.toFixed(2) ?? ''
                                                                    )}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    VAT fee:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    {addCommasToNumbers(
                                                                        quoteDetailsData?.quotation.vatFee.toFixed(2) ?? ''
                                                                    )}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    Admin fee:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    {addCommasToNumbers(
                                                                        quoteDetailsData?.quotation.adminFee.toFixed(2) ?? ''
                                                                    )}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    Total Premium:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    {addCommasToNumbers(
                                                                        quoteDetailsData?.quotation.totalPremium.toFixed(2) ?? ''
                                                                    )}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography align="right" color="primary" variant="subtitle1">
                                                                    (All amounts are in USD).
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </SubCard>
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                </Grid>
            </MainCard>
        </>
    );
};

export default ViewQuoteDetails;
